import React, { useState } from 'react';

import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { Toast } from 'src/common/components';
import { getUserInitial } from 'src/common/utils';

import { createUser } from '../api';
import { USERNAME_PATTERN, EMAIL_PATTERN, Action } from '../constants';
import { useAccountProfileContext } from '../context';

type UsersItemCreateType = {
  handleSetIsCreatingUser: () => void;
};

type NewUserType = {
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  password: string;
  confirm_password: string;
  is_account_manager: boolean;
  can_switch_accounts: boolean;
};

const UsersItemCreate: React.FC<UsersItemCreateType> = ({
  handleSetIsCreatingUser,
}: UsersItemCreateType) => {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
    setError,
  } = useForm<NewUserType>();
  const [isSaving, setIsSaving] = useState(false);
  const { state, dispatch } = useAccountProfileContext();
  const { id: accountId } = state.account;

  const { first_name, last_name, username } = watch();
  const userInitial = getUserInitial({ first_name, last_name, username });

  const handleCreateUser = async (data: NewUserType): Promise<void> => {
    try {
      setIsSaving(true);
      const response = await createUser({
        ...data,
        account: accountId,
        is_active: true,
      });
      dispatch({ type: Action.CREATE_NEW_USER, user: response.data });
      handleSetIsCreatingUser();
      Toast.success('The user was created successfully.');
    } catch (err) {
      const error = err as AxiosError<{ username: string[]; password: string[] }>;
      const dataError = error.response?.data;
      if (dataError?.username) {
        setError('username', { type: 'server', message: dataError.username[0] });
      }
      if (dataError?.password) {
        setError('password', { type: 'server', message: dataError.password[0] });
      }
      Toast.error('There was an error creating the user.');
    } finally {
      setIsSaving(false);
    }
  };

  const validatePassword = (value: string): boolean => {
    const { password } = getValues();
    if (password !== value) return false;
    return true;
  };

  return (
    <form className="flex flex-col p-6" onSubmit={handleSubmit(handleCreateUser)}>
      <h2 className="mt-1">Create a new user</h2>
      <div className="flex my-8">
        <div className="flex w-12 h-12 rounded-full bg-primary-1.0">
          <h3 className="m-auto text-white-1.0">{userInitial}</h3>
        </div>
        <p className="text-gray-1.0 font-semibold my-auto ml-2">User:</p>
        <p className="text-gray-1.0 font-semibold ml-2 my-auto">{username || 'Not filled yet'}</p>
      </div>
      <label htmlFor="username">
        <p className="input-label">Username</p>
        <input
          id="username"
          className="input sm:w-1/2 w-full mt-1 transition duration-150 ease-in-out"
          {...register('username', {
            required: true,
            pattern: USERNAME_PATTERN,
            minLength: 4,
            maxLength: 16,
          })}
          defaultValue=""
          data-testid="username"
          autoComplete="off"
        />
        {errors.username && errors.username.type === 'required' && (
          <p className="error-text"> The username is required</p>
        )}
        {errors.username &&
          ['required', 'minLength', 'maxLength'].includes(errors.username.type) && (
            <p className="error-text">
              The user should have at least 4 characters, no more than 16 and only letters, digits
              and @. + - _
            </p>
          )}
        {errors.username && errors.username.type === 'server' && (
          <p className="error-text">{errors.username.message}</p>
        )}
      </label>
      <label htmlFor="first_name" className="mt-4">
        <p className="input-label">First name</p>
        <input
          id="first_name"
          className="input sm:w-1/2 w-full mt-1 transition duration-150 ease-in-out"
          {...register('first_name', { required: true })}
          defaultValue=""
          data-testid="first-name"
          autoComplete="off"
        />
        {errors.first_name && <p className="error-text"> The first name is required</p>}
      </label>
      <label htmlFor="last_name" className="mt-4">
        <p className="input-label">Last name</p>
        <input
          id="last_name"
          className="input sm:w-1/2 w-full mt-1 transition duration-150 ease-in-out"
          {...register('last_name', { required: true })}
          defaultValue=""
          data-testid="last-name"
          autoComplete="off"
        />
        {errors.last_name && <p className="error-text"> The last name is required</p>}
      </label>
      <label htmlFor="email" className="mt-4">
        <p className="input-label">Email</p>
        <input
          id="email"
          className="input sm:w-1/2 w-full mt-1 transition duration-150 ease-in-out"
          {...register('email', { required: true, pattern: EMAIL_PATTERN })}
          defaultValue=""
          data-testid="email"
          autoComplete="off"
        />
        {errors.email && errors.email.type === 'required' && (
          <p className="error-text"> The email is required</p>
        )}
        {errors.email && errors.email.type === 'pattern' && (
          <p className="error-text">Please enter a valid email</p>
        )}
      </label>
      <label htmlFor="password" className="mt-4">
        <p className="input-label">Password</p>
        <input
          id="password"
          className="input sm:w-1/2 w-full mt-1 transition duration-150 ease-in-out"
          {...register('password', { required: true })}
          defaultValue=""
          data-testid="password"
          autoComplete="off"
          type="password"
        />
        {errors.password && <p className="error-text"> The password is required</p>}
      </label>
      <label htmlFor="confirm_password" className="mt-4">
        <p className="input-label">Confirm password</p>
        <input
          id="confirm_password"
          className="input sm:w-1/2 w-full mt-1 transition duration-150 ease-in-out"
          {...register('confirm_password', { required: true, validate: validatePassword })}
          defaultValue=""
          data-testid="confirm-password"
          autoComplete="off"
          type="password"
        />
        {errors.confirm_password && errors.confirm_password.type === 'required' && (
          <p className="error-text"> The confirm password is required</p>
        )}
        {errors.confirm_password && errors.confirm_password.type === 'validate' && (
          <p className="ml-1 small font-semibold text-red-1.0">Password does not match</p>
        )}
      </label>
      <label htmlFor="is_account_manager" className=" mt-4">
        <div className="flex">
          <p className="input-label">Is account manager?</p>
          <input
            id="is_account_manager"
            className="my-auto ml-4 transition duration-150 ease-in-out"
            {...register('is_account_manager')}
            data-testid="is-account-manager"
            type="checkbox"
          />
        </div>
        <p className="helper-text">True if the user is an admin for this account.</p>
      </label>
      <label htmlFor="is_account_manager" className=" mt-4">
        <div className="flex">
          <p className="input-label">Can switch accounts?</p>
          <input
            id="can_switch_accounts"
            className="my-auto ml-4 transition duration-150 ease-in-out"
            {...register('can_switch_accounts')}
            data-testid="can-switch-accounts"
            type="checkbox"
          />
        </div>
        <p className="helper-text">True if the user is allowed to change between accounts.</p>
      </label>
      <div className="flex mt-8 ml-auto">
        <button
          className="btn btn-ghost"
          type="button"
          onClick={handleSetIsCreatingUser}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          className="btn btn-secondary ml-4"
          type="submit"
          disabled={isSaving}
          data-testid="save-user"
        >
          {isSaving ? 'Saving...' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default UsersItemCreate;
